/**
 * MessageType — the canonical vocabulary for `message_type` wire values.
 *
 * 132 call sites compare `message_type` against bare string literals (25+
 * distinct values); a typo in any of them fails silently (see the Module 0
 * bug class in docs/domain-refactor-roadmap.md). All values live here as
 * constants; recurring comparison CLUSTERS live here as predicates.
 *
 * The `lint:messages` gate bans new bare literal comparisons outside
 * src/domain/ — reach for MESSAGE_TYPES.<NAME> or a predicate instead.
 *
 * Roadmap: docs/domain-refactor-roadmap.md (Module 6). Linear: INH-5151.
 */

export const MESSAGE_TYPES = {
  // Conversation
  CHAT: "chat",
  TEXT_DELTA: "text_delta",
  UPDATE_LOADING_MESSAGE: "update_loading_message",
  QUICK_ACTIONS: "quick_actions",
  QUICK_ACTION_BUTTON_MESSAGES: "quick_action_button_messages",
  MEMORY_UPDATED: "memory_updated",
  INHOUSE_PREV_CHAT_SUMMARY: "inhouse_prev_chat_summary",
  // Documents
  DOCUMENT_START: "document_start",
  DOCUMENT: "document",
  DOCUMENT_GENERATED: "document_generated",
  LOCKED_DOCUMENT_GENERATED: "locked_document_generated",
  // Attachments
  ATTACHMENT: "attachment",
  EMAIL_ATTACHMENT: "email_attachment",
  // GPT-context landing (ChatGPT → Inhouse handoff)
  LAW_GPT_SUMMARY: "law_gpt_summary",
  LAW_GPT_SUMMARY_ASK: "law_gpt_summary_ask",
  LAW_GPT_SUMMARY_DRAFT: "law_gpt_summary_draft",
  LAW_GPT_ADDITIONAL_DETAILS: "law_gpt_additional_details",
  // Legal review / consultation
  LEGAL_REVIEW_MESSAGE: "legal_review_message",
  PAYMENT_CONFIRMATION: "payment_confirmation",
  // Privileged chats
  PRIVILEGED_CHAT_STARTED: "privileged_chat_started",
  PRIVILEGED_CHAT_ENDED: "privileged_chat_ended",
  // Terminal / blocking states
  REGENERATE_AI_RESPONSE: "regenerate_ai_response",
  AI_CONTEXT_LENGTH_EXCEEDED: "ai_context_length_exceeded",
  FREE_USER_MESSAGE_LIMIT_REACHED: "free_user_message_limit_reached",
  SUBSCRIBED_MESSAGE_LIMIT_REACHED: "subscribed_message_limit_reached",
  FREE_USER_ATTACHMENT_LIMIT_REACHED: "free_user_attachment_limit_reached",
  HIGH_CONTEXT_WARNING: "high_context_warning",
  DEPRECATED_THREAD_MESSAGE: "deprecated_thread_message",
} as const;

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

/**
 * Message types that put a thread into a permanently blocked state where
 * polling /api/thread/{id}/status can never resolve the blockage — only
 * explicit user action (Regenerate, subscribe, start new chat) can.
 * Single source of truth for every status poller; moved here from
 * `utils/threadTerminalState.ts` (which now delegates).
 */
export const TERMINAL_MESSAGE_TYPES = [
  MESSAGE_TYPES.REGENERATE_AI_RESPONSE,
  MESSAGE_TYPES.AI_CONTEXT_LENGTH_EXCEEDED,
  MESSAGE_TYPES.FREE_USER_MESSAGE_LIMIT_REACHED,
  MESSAGE_TYPES.SUBSCRIBED_MESSAGE_LIMIT_REACHED,
  MESSAGE_TYPES.DEPRECATED_THREAD_MESSAGE,
] as const;

export type TerminalMessageType = (typeof TERMINAL_MESSAGE_TYPES)[number];

const GPT_SUMMARY_TYPES: readonly string[] = [
  MESSAGE_TYPES.LAW_GPT_SUMMARY,
  MESSAGE_TYPES.LAW_GPT_SUMMARY_ASK,
  MESSAGE_TYPES.LAW_GPT_SUMMARY_DRAFT,
];

const DOCUMENT_TYPES: readonly string[] = [
  MESSAGE_TYPES.DOCUMENT,
  MESSAGE_TYPES.DOCUMENT_GENERATED,
  MESSAGE_TYPES.LOCKED_DOCUMENT_GENERATED,
];

const ATTACHMENT_TYPES: readonly string[] = [MESSAGE_TYPES.ATTACHMENT, MESSAGE_TYPES.EMAIL_ATTACHMENT];

/** Any of the three law_gpt_summary variants (the ChatGPT-handoff landing turn). */
export const isGptSummaryMessage = (messageType: string | null | undefined): boolean =>
  !!messageType && GPT_SUMMARY_TYPES.includes(messageType);

/** Document-bearing messages (draft cards, locked and unlocked). */
export const isDocumentMessage = (messageType: string | null | undefined): boolean =>
  !!messageType && DOCUMENT_TYPES.includes(messageType);

/** User-uploaded attachment messages (file and email). */
export const isAttachmentMessage = (messageType: string | null | undefined): boolean =>
  !!messageType && ATTACHMENT_TYPES.includes(messageType);

/**
 * The thread's last message indicates a permanently blocked state that cannot
 * be resolved by polling /status. Checked against
 * `threadData.lastMessage.message_type` (the CURRENT terminal message), not
 * historical occurrences.
 */
export const isThreadInTerminalState = (lastMessageType?: string | null): boolean =>
  !!lastMessageType && (TERMINAL_MESSAGE_TYPES as readonly string[]).includes(lastMessageType);

/**
 * Message types that are "transparent" to quick action button visibility —
 * a quick action sitting just before one of these is still treated as the
 * effective last actionable message.
 */
export const TRANSPARENT_LAST_TYPES = [
  MESSAGE_TYPES.LEGAL_REVIEW_MESSAGE,
  MESSAGE_TYPES.QUICK_ACTIONS,
  MESSAGE_TYPES.QUICK_ACTION_BUTTON_MESSAGES,
  MESSAGE_TYPES.REGENERATE_AI_RESPONSE,
  MESSAGE_TYPES.AI_CONTEXT_LENGTH_EXCEEDED,
  MESSAGE_TYPES.FREE_USER_MESSAGE_LIMIT_REACHED,
  MESSAGE_TYPES.SUBSCRIBED_MESSAGE_LIMIT_REACHED,
  MESSAGE_TYPES.FREE_USER_ATTACHMENT_LIMIT_REACHED,
  MESSAGE_TYPES.HIGH_CONTEXT_WARNING,
] as const;

/** Check if the last message in a messages array is a regenerate_ai_response. */
export const isLastMessageRegenerateResponse = (messages: any[]): boolean =>
  messages?.[messages.length - 1]?.message_type === MESSAGE_TYPES.REGENERATE_AI_RESPONSE;
